// DRAFT legal content for owner review. NOT attorney-reviewed legal advice.
// Blocks with type "decision" mark business/legal decisions the owner must make
// or a lawyer must confirm before the site goes live.

export const LEGAL = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    updated: "September 9, 2026",
    intro:
      "This Privacy Policy explains what information Marella Bolognesi (\u201cwe\u201d, \u201cus\u201d) collects when you use this website and Il Salotto Digitale, how we use it, and the choices you have. It reflects how the site actually works today.",
    sections: [
      {
        h: "Information we collect",
        p: [
          "Account details: when you create an account, we collect your name, email address and a securely hashed password. We never store your password in readable form.",
          "Google sign-in: if you choose \u201cContinue with Google\u201d, Google confirms your identity and shares your name and email address with us so we can sign you in or create your account. We do not receive your Google password.",
          "Purchases without an account: when you buy a product before creating an account, we create one for you using the name and email address you provided to Stripe at checkout, and email you a secure, single-use activation link to set your password. No access is granted until you activate it.",
          "Purchases: when you buy a course, payment is processed by Stripe. We store a record of the transaction (amount, course, status and date) and which course was added to your library. We do not receive or store your full card number \u2014 card details are handled entirely by Stripe.",
          "Lead and newsletter details: when you request a complimentary resource (such as the Hurricane Preparedness Checklist) or subscribe to \u201cNotes from Il Salotto\u201d, we collect your first name and email address.",
          "Contact enquiries: when you use a contact form, we collect the name, email and message you submit.",
          "Course activity: when you take a course, we record your lesson progress so you can pick up where you left off.",
          "Basic technical data: our servers keep standard logs (such as IP address and time of requests) needed to operate the site securely, including limiting repeated failed sign-in attempts.",
        ],
      },
      {
        h: "How we use your information",
        p: [
          "To provide your account, deliver the courses and resources you request or purchase, and save your progress.",
          "To send transactional emails: account welcome, delivery of complimentary resources, purchase confirmations and password-reset messages.",
          "To send occasional \u201cNotes from Il Salotto\u201d updates where you have subscribed. You can unsubscribe at any time.",
          "To respond to your enquiries and to operate, secure and improve the site.",
        ],
      },
      {
        h: "Cookies and analytics",
        p: [
          "We use a small amount of local browser storage to keep you signed in to your account. This is essential to the member area and is not used to track you across other websites.",
          "We also use private, first-party website analytics to understand how visitors use the site. Your browser stores a randomly generated identifier that is not linked to your name, email address or account. Using this identifier, we count visits, sessions and pages viewed, note the website or search engine that referred you to us, and record whether you are using a phone, tablet or desktop.",
          "This analytics information is used to understand and improve the website and Il Salotto Digitale. It is not used for advertising, profiling across other websites, or sold to advertisers.",
          "We do not use Google Analytics, Meta Pixel or third-party advertising or cross-site tracking cookies. We also keep aggregate counts of certain actions inside Il Salotto Digitale, such as product views, purchase-button clicks and resource opens, to understand what content and products are useful.",
          "You can clear the anonymous analytics identifier by clearing your browser\u2019s site data. The website will continue to function normally.",
          "Our first-party analytics run within our website infrastructure; no separate third-party analytics platform receives this analytics data.",
        ],
      },
      {
        h: "Service providers we share data with",
        p: [
          "Stripe \u2014 to process payments securely.",
          "Resend (our email provider) \u2014 to deliver the transactional and newsletter emails described above.",
          "Our hosting and database providers \u2014 to run the website and store your account and content.",
          "We do not sell your personal information.",
        ],
      },
      {
        h: "Data retention",
        p: [
          "We keep account, purchase and course-progress records for as long as your account is active or as needed to provide the service and meet legal and accounting obligations.",
          "Complimentary-resource download links are time-limited and expire automatically.",
        ],
      },
      {
        h: "Your choices and rights",
        p: [
          "You can unsubscribe from newsletter emails at any time using the link in those emails.",
          "You can request access to, correction of, or deletion of your personal information by contacting us.",
        ],
        decision:
          "OWNER / LEGAL DECISION: Confirm which privacy rights framework(s) apply to you (e.g. GDPR for EU/UK visitors, and relevant US state laws). A lawyer should confirm the exact rights language, your legal basis for processing, and any \u201cData Controller\u201d details before launch.",
      },
      {
        h: "Contact",
        p: [
          "For any privacy question or request, please contact us at info@marellabolognesi.com, or use the contact page on this site.",
        ],
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms of Service",
    updated: "Draft — pending review",
    intro:
      "These Terms govern your use of this website, your account, and the courses, guides and resources offered through Il Salotto Digitale. By using the site or creating an account, you agree to these Terms.",
    sections: [
      {
        h: "Accounts",
        p: [
          "You are responsible for keeping your login details secure and for activity under your account. You must provide accurate information when registering.",
          "You must be old enough to enter into a contract in your place of residence to make a purchase.",
        ],
      },
      {
        h: "Purchases and access",
        p: [
          "Courses and certain resources are digital products. When you purchase a course, we grant you a personal, non-transferable licence to access it through your account library.",
          "Prices are shown on the relevant product page and are charged in US dollars unless stated otherwise. Payment is processed by Stripe.",
          "Access is provided for your own individual use. You may not share your account, resell, redistribute or publicly post the course materials or downloadable resources.",
        ],
      },
      {
        h: "Complimentary resources",
        p: [
          "Some resources are offered at no cost in exchange for your email address. Download links for these are time-limited and intended for your personal use.",
        ],
      },
      {
        h: "Intellectual property",
        p: [
          "All course content, guides, downloadable files, text, images and branding on this site are owned by Marella Bolognesi or licensed to us, and are protected by intellectual-property laws. You may not copy, reproduce or distribute them except as expressly permitted.",
        ],
      },
      {
        h: "Nature of the content — no guarantees",
        p: [
          "The courses and materials are educational. They are a practical introduction to the profession and are not a certification, qualification, or a guarantee of employment or any particular outcome.",
        ],
      },
      {
        h: "Acceptable use",
        p: [
          "You agree not to misuse the site, attempt to gain unauthorised access, disrupt its operation, or use it for any unlawful purpose.",
        ],
      },
      {
        h: "Cancellation and refunds",
        p: [
          "Refunds are handled in accordance with our Refund Policy, which forms part of these Terms.",
        ],
      },
      {
        h: "Limitation of liability & disclaimers",
        p: [
          "The site and content are provided on an \u201cas is\u201d basis to the fullest extent permitted by law.",
        ],
        decision:
          "OWNER / LEGAL DECISION: The limitation-of-liability, warranty-disclaimer, indemnity and dispute-resolution wording should be confirmed by a lawyer and tailored to your risk tolerance before launch.",
      },
      {
        h: "Governing law",
        p: [
          "These Terms are governed by the laws of the State of Florida, United States, without regard to its conflict-of-laws principles. This provision remains subject to professional legal review.",
        ],
      },
      {
        h: "Changes and contact",
        p: [
          "We may update these Terms from time to time; the current version will always be posted here. For questions, please use the contact page.",
        ],
      },
    ],
  },

  refund: {
    slug: "refund",
    title: "Refund Policy",
    updated: "August 2026",
    intro:
      "This Refund Policy applies to digital products (courses, guides and downloadable resources) purchased through Il Salotto Digitale. Please read it before purchasing, as our products provide immediate access to digital content.",
    sections: [
      {
        h: "Digital products and immediate access",
        p: [
          "Our courses, guides and resources are digital products. When you complete a purchase, access is granted to your account immediately and downloadable materials become available to you straight away.",
          "You will see a reminder at checkout that the product is digital and provides immediate access.",
        ],
      },
      {
        h: "All sales are final",
        p: [
          "Because our courses, guides and digital resources provide immediate access to proprietary digital content and downloadable materials, all sales are final once access has been granted.",
        ],
      },
      {
        h: "Technical or billing issues",
        p: [
          "We want your experience to be a good one. If you experience a technical issue that prevents you from accessing your purchase, are charged incorrectly, or believe there has been a billing error, please contact us so we can review and resolve the issue.",
          "The quickest way to reach us is through the contact page, using the email associated with your account and details of the product purchased.",
        ],
      },
      {
        h: "How refunds are issued",
        p: [
          "Where we determine that a refund is appropriate (for example, a duplicate charge or a genuine billing error), it is returned to your original payment method via Stripe. The time for funds to appear depends on your bank or card provider.",
        ],
      },
      {
        h: "Complimentary resources",
        p: [
          "Free resources involve no payment and are therefore not subject to refunds.",
        ],
      },
    ],
  },
};
